<template>
  <div class="content-container">
    <ListTopBar
      :batchSelectActive="batchSelectActive"
      :allItemsAreSelected="allItemsAreSelected"
      :sections="sections"
      :rowMenuActive="!!contextMenu.displayFor.id"
      :selectedLength="selectedItems.length"
      @hide-row-menu="hideContextMenu"
      @toggle-select-all="toggleSelectAllItems"
      @update-section="updateSections"
      @moveSelected="initiateMoveItems"
      @deleteSelected="deleteSelectedItems" />

    <div
      v-if="$route.name === 'Collection' && !sortedItems.length && !searchIsActive"
      class="no-items">
      No items in this collection. <span class="add-note-or-card">Add <span @click="$emit('add-card')">card</span> or <span @click="$emit('add-note')">note</span></span>
    </div>

    <!-- CARD LIST -->
    <div
      v-scroll="storeScrollPosition"
      class="items-list"
      ref="itemsList">
      <div
        class="sectioned-items"
        v-for="(section, index) in sections"
        :key="index">
        <span class="section-header">
          {{ section }}
          <span
            class="section-header-select"
            @click="selectSection(section)">- (de)select all</span>
        </span>
        <transition-group name="collection-list">
          <ItemRow
            v-for="(item, index) in itemsToDisplay.sectioned[section]"
            :key="item.id ? `${item.type}${item.id}` : -index"
            :item="item"
            :batchSelectActive="batchSelectActive"
            :itemIsSelected="!!selectedItems.find(el => el.id === item.id && el.type === item.type)"
            :selectCount="selectCount"
            :selectIdObj="{ id: selectId, itemId: item.id, itemType: item.type }"
            :contextMenu="contextMenu"
            :sections="sections"
            @move-item="initiateMoveItems({ id: item.id, type: item.type })"
            @move-selected="initiateMoveItems"
            @update-section="updateSections"
            @delete-selected="deleteSelectedItems"
            @show-context-menu="displayContextMenu"
            @hide-menu="hideContextMenu"
            class="collection-list-item" />
        </transition-group>
        <span
          v-if="itemsToDisplay.unsectioned.length"
          class="section-header no-section">
          none
          <span
            class="section-header-select"
            @click="selectSection(null)">- (de)select all</span>
        </span>
      </div>
      <transition-group name="collection-list">
        <ItemRow
          v-for="(item, index) in itemsToDisplay.unsectioned"
          :key="item.id ? `${item.type}${item.id}` : -index"
          :item="item"
          :batchSelectActive="batchSelectActive"
          :itemIsSelected="!!selectedItems.find(el => el.id === item.id && el.type === item.type)"
          :selectCount="selectCount"
          :selectIdObj="{ id: selectId, itemId: item.id, itemType: item.type }"
          :contextMenu="contextMenu"
          :sections="sections"
          @move-item="initiateMoveItems({ id: item.id, type: item.type })"
          @move-selected="initiateMoveItems"
          @update-section="updateSections"
          @delete-selected="deleteSelectedItems"
          @show-context-menu="displayContextMenu"
          @hide-menu="hideContextMenu"
          class="collection-list-item" />
      </transition-group>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import parseISO from 'date-fns/parseISO';
import compareAsc from 'date-fns/compareAsc';
import ItemRow from '@/components/AppView/CollectionView/ItemRow.vue';
import ListTopBar from '@/components/AppView/CollectionView/ListTopBar.vue';
import ModalMove from '@/components/AppView/CollectionView/ModalMove.vue';
// eslint-disable-next-line import/extensions
import { EventBus } from '@/EventBus.js';

const debounce = require('lodash/debounce');

export default {
  name: 'CollectionView',
  components: {
    ItemRow,
    ListTopBar,
    ModalMove, // eslint-disable-line
  },
  directives: {
    scroll: {
      inserted: function (el, binding) { // eslint-disable-line 
        let f = function (evt) { // eslint-disable-line 
          if (binding.value(evt, el)) {
            el.removeEventListener('scroll', f);
          }
        };
        el.addEventListener('scroll', f);
      },
    },
  },
  data() {
    return {
      contextMenu: {
        displayFor: {
          type: null,
          id: null,
        },
        top: null,
        bottom: null,
        left: null,
        right: null,
      },
    };
  },
  computed: {
    ...mapState({
      searchIsActive: state => state.appState.search.isActive,
      searchTerms: state => state.search.terms,
      selectedCollection: state => state.collections.selectedCollection,
      sortSetting: state => state.appState.localSettings.collectionViewSortSetting,
      allSelectedItems: state => state.appState.selectedItems,
      lastKeywordList: state => state.appState.lastKeywordList,
      lastStarList: state => state.appState.lastStarList,
      scrollState: state => state.appState.scrollState,
    }),
    ...mapGetters({
      cards: 'collections/getCardsOfSelectedCollection',
      notes: 'collections/getNotesOfSelectedCollection',
      v_selectedItems: 'appState/getSelectedItems',
      searchResults: 'search/searchResults',
      cardsByKeyword: 'cards/cardsByKeyword',
      cardsByStars: 'cards/cardsByStars',
      notesByStars: 'notes/notesByStars',
    }),
    allItemsAreSelected() {
      // Calculate number of items in view: start with unsectioned length, then add length of each section
      let totalNumberOfItemsDisplayed = this.itemsToDisplay.unsectioned.length;
      Object.values(this.itemsToDisplay.sectioned).forEach((entry) => { totalNumberOfItemsDisplayed += entry.length; });
      // Determine and return result
      return this.selectedItems.length === totalNumberOfItemsDisplayed;
    },
    selectCount() {
      return this.selectedItems.length;
    },
    selectedItems() {
      // When on keywordList or StarList, selectedItems need to be looked up manually since Vuex does not know about this (search or collection is done by Vuex)
      if (this.$route.name === 'KeywordList') {
        const keywordlistEntry = this.allSelectedItems.find(entry => entry.id === 'keywordlist');
        if (keywordlistEntry) return keywordlistEntry.items;
        return [];
      }
      if (this.$route.name === 'StarList') {
        const starlistEntry = this.allSelectedItems.find(entry => entry.id === 'starlist');
        if (starlistEntry) return starlistEntry.items;
        return [];
      }

      return this.v_selectedItems.items;
    },
    batchSelectActive() {
      return !!this.selectedItems.length;
    },
    selectId() {
      if (this.searchIsActive && this.searchTerms.length) return 'search';
      if (this.$route.name === 'KeywordList') return 'keywordlist';
      if (this.$route.name === 'StarList') return 'starlist';
      return this.selectedCollection;
    },
    sections() {
      // We want to have the default 'cards' and 'notes' sections always on bottom, except when there are no cards/notes with those sections. Rest is alphabetical.
      const sections = Object.keys(this.itemsToDisplay.sectioned);
      const hasCardsSection = sections.includes('cards');
      const hasNotesSection = sections.includes('notes');

      if (hasCardsSection) {
        const indexOfCards = sections.findIndex(section => section === 'cards');
        sections.splice(indexOfCards, 1);
      }
      if (hasNotesSection) {
        const indexOfNotes = sections.findIndex(section => section === 'notes');
        sections.splice(indexOfNotes, 1);
      }

      sections.sort();
      if (hasCardsSection) sections.push('cards');
      if (hasNotesSection) sections.push('notes');

      return sections;
    },
    itemsToDisplay() {
      // With search active, return the search results as unsectioned items (using sections to group search results may not make much sense, e.g. if sections have the same
      // 'name' but with a different semantic meaning across collections; if user feedback indicates otherwise this can be changed)
      if (this.searchIsActive && this.searchTerms.length) return { sectioned: {}, unsectioned: this.searchResults };

      // Divide the items into sections
      const items = { sectioned: {}, unsectioned: [] };
      this.sortedItems.forEach((item) => {
        if (!item.section) items.unsectioned.push(item);
        else if (item.section in items.sectioned) items.sectioned[item.section].push(item);
        else items.sectioned[item.section] = [item];
      });
      return items;
    },
    sortedItems() {
      let itemsToSort = [];
      // Populate itemsToSort (based on whether we're viewing a Keyword-/StarList or simply a collection)
      if (this.$route.name === 'KeywordList') {
        // When keywordList is empty, return empty array (happens when deselecting all)
        if (!this.lastKeywordList) return [];

        const keywords = this.lastKeywordList.split('/');
        // First two are not keywords, so delete
        keywords.shift();
        keywords.shift();
        // Gather items based on keywords
        keywords.forEach((keyword) => {
          // If an entry for this keyword does not exist, that means the keyword is not used by any card anymore. Therefore remove it.
          if (!this.cardsByKeyword[keyword]) {
            this.$removeKeywordFromList(keyword);
            return;
          }

          const temp = this.cardsByKeyword[keyword].slice();
          // To do: add notesByKeyword (once you can use keywords for notes)
          itemsToSort = itemsToSort.concat(temp);
        });
        // Remove duplicates (cards may have multiple keywords)
        itemsToSort = [...new Set(itemsToSort)];
      }
      else if (this.$route.name === 'StarList') {
        // When starList is empty, return empty array (happens when deselecting all)
        if (!this.lastStarList) return [];

        const stars = this.lastStarList.split('/'); // returns in string format, but is fine for only used for looking up object keys
        // First two are not stars, so delete
        stars.shift();
        stars.shift();
        // Gather cards based on stars
        stars.forEach((star) => {
          const cardsWithXStars = this.cardsByStars[star].slice();
          const notesWithXStars = this.notesByStars[star].slice();
          itemsToSort = itemsToSort.concat(cardsWithXStars);
          itemsToSort = itemsToSort.concat(notesWithXStars);
        });
      }
      else {
        const cards = (this.cards && this.cards.slice()) || [];
        const notes = (this.notes && this.notes.slice()) || [];
        itemsToSort = cards;
        itemsToSort = itemsToSort.concat(notes);
      }

      if (this.sortSetting === 'alphabetical-asc') {
        const sorted = itemsToSort.sort((a, b) => {
          const titleA = a.title.toUpperCase();
          const titleB = b.title.toUpperCase();
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        return sorted;
      }
      if (this.sortSetting === 'alphabetical-desc') {
        const sorted = itemsToSort.sort((a, b) => {
          const titleA = a.title.toUpperCase();
          const titleB = b.title.toUpperCase();
          if (titleA < titleB) return 1;
          if (titleA > titleB) return -1;
          return 0;
        });
        return sorted;
      }
      if (this.sortSetting === 'stars-asc') {
        const sorted = itemsToSort.sort((a, b) => {
          const order = b.stars - a.stars;
          if (order) return order;

          // If equal, sort alphabetically
          const titleA = a.title.toUpperCase();
          const titleB = b.title.toUpperCase();
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        return sorted;
      }
      if (this.sortSetting === 'stars-desc') {
        const sorted = itemsToSort.sort((a, b) => {
          const order = a.stars - b.stars;
          if (order) return order;

          // If equal, sort alphabetically
          const titleA = a.title.toUpperCase();
          const titleB = b.title.toUpperCase();
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        return sorted;
      }
      if (this.sortSetting === 'modified-asc') {
        const sorted = itemsToSort.sort((a, b) => compareAsc(parseISO(b.dateLastModified), parseISO(a.dateLastModified)));
        return sorted;
      }
      if (this.sortSetting === 'modified-desc') {
        const sorted = itemsToSort.sort((a, b) => compareAsc(parseISO(a.dateLastModified), parseISO(b.dateLastModified)));
        return sorted;
      }
      if (this.sortSetting === 'created') {
        const sorted = itemsToSort.sort((a, b) => compareAsc(parseISO(b.dateCreated), parseISO(a.dateCreated)));
        return sorted;
      }

      return itemsToSort; // Should not end up here, right? Used to return this.cards...
    },
  },
  methods: {
    ...mapActions({
      unselectItemsFrom: 'appState/unselectItemsFrom',
      selectItems: 'appState/selectItems',
      addSelectedItems: 'appState/addSelectedItems',
      toggleSelectedItem: 'appState/toggleSelectedItem',
      updateCollectionNotes: 'notes/updateCollectionNotes',
      moveCollectionNotes: 'notes/moveCollectionNotes',
      deleteCollectionNotes: 'notes/deleteCollectionNotes',
      v_storeScrollPosition: 'appState/storeScrollPosition',
    }),
    storeScrollPosition: debounce(function(evt, el) { // eslint-disable-line
      // this.selectID can return 'search' or 'keywordslist' and stuff. Those should reset on clearing search / keywords / etc though.
      // In mounted(), this.selectedCollection is used since selectID is undefined.
      this.v_storeScrollPosition({ type: 'collectionView', id: this.selectId, scrollPosition: el.scrollTop });
    }, 50),
    selectSection(section) {
      let items;
      // Gather items of the section we are dealing with
      if (section === null) items = this.itemsToDisplay.unsectioned.map(item => ({ id: item.id, type: item.type }));
      else items = this.itemsToDisplay.sectioned[section].map(item => ({ id: item.id, type: item.type }));

      // Check if all items are selected. If all items are selected, deselect all from that section. Else select all from that section that aren't selected yet.
      const notAllCardsAreSelected = items.some(item => !this.selectedItems.find(selectedItem => (selectedItem.type === item.type && selectedItem.id === item.id)));

      if (notAllCardsAreSelected) {
        // Select all cards in the section that are not selected yet (by filtering out those that are already selected) and add to selectedItems
        items = items.filter(item => !this.selectedItems.find(selectedItem => (selectedItem.type === item.type && selectedItem.id === item.id)));
        this.addSelectedItems({ id: this.selectId, items });
      }
      else {
        // Deselect this section. Filter out cardIds of this section from selectedItems, then select resulting items
        const filteredItems = this.selectedItems.filter(selectedItem => !items.find(item => (selectedItem.type === item.type && selectedItem.id === item.id)));
        this.selectItems({ id: this.selectId, items: filteredItems });
      }
    },
    checkHideContextMenu(e) {
      if (this.contextMenu.displayFor.id) {
        e.stopPropagation();
        this.hideContextMenu();
      }
    },
    updateSections(section) {
      this.selectedItems.forEach(({ id, type }) => {
        if (type === 'card') {
          this.$updateCard({
            id,
            data: {
              section,
            },
          });
        }
        else if (type === 'note') {
          const dateLastModified = this.$getCurrentUTC();
          this.updateCollectionNotes({
            noteId: id,
            section,
            dateLastModified,
          });
        }
      });
      this.unselectItemsFrom(this.selectId);
      this.hideContextMenu();
    },
    displayContextMenu(e, coords, { type, id }) {
      // Reset position
      this.contextMenu.top = null;
      this.contextMenu.bottom = null;
      this.contextMenu.left = null;
      this.contextMenu.right = null;

      this.contextMenu.displayFor.id = id;
      this.contextMenu.displayFor.type = type;
      if (e.target.className.includes('collection-list-item-menu-icon')) {
        // Menu opened via horizontal dots menu, only need to check for vertical screen overflow
        if (e.clientY > window.innerHeight - 210) this.contextMenu.bottom = true; // Default value of 1rem is applied in CardRow.vue
        return;
      }

      // Right click menu, get position of row element
      const { right: elRight, left: elLeft, top: elTop, bottom: elBottom } = coords;
      // Calculate and set positions
      if (e.clientX > window.innerWidth - 170) this.contextMenu.right = elRight - e.clientX;
      else this.contextMenu.left = e.clientX - elLeft;
      if (e.clientY > window.innerHeight - 210) this.contextMenu.bottom = elBottom - e.clientY;
      else this.contextMenu.top = e.clientY - elTop;
    },
    hideContextMenu() {
      this.contextMenu.displayFor.id = null;
      this.contextMenu.displayFor.type = null;
      this.contextMenu.top = null;
      this.contextMenu.bottom = null;
      this.contextMenu.left = null;
      this.contextMenu.right = null;
    },
    toggleSelectAllItems() {
      if (this.allItemsAreSelected) this.unselectItemsFrom(this.selectId);
      else {
        // First gather all displayed items
        const itemsToSelect = this.itemsToDisplay.unsectioned.map(item => ({ type: item.type, id: item.id }));
        Object.values(this.itemsToDisplay.sectioned).forEach((section) => {
          section.forEach((item) => {
            itemsToSelect.push({ type: item.type, id: item.id });
          });
        });

        this.selectItems({ id: this.selectId, items: itemsToSelect });
      }
    },
    deleteSelectedItems() {
      const itemsAreSelected = !!this.selectedItems.length;
      if (!itemsAreSelected) {
        this.$buefy.toast.open('No items selected!');
        return;
      }

      this.$buefy.dialog.confirm({
        message: `About to delete ${this.selectedItems.length} item(s). Are you sure?<br><br>
                  <em style="font-size: 0.85rem;">Note that this cannot be undone.</em><br>`,
        cancelText: 'Cancel',
        confirmText: 'Yes',
        type: 'is-danger',
        hasIcon: false,
        onConfirm: () => {
          this.selectedItems.forEach((item) => {
            if (item.type === 'card') this.$deleteCard(item.id);
            else if (item.type === 'note') this.deleteCollectionNotes({ noteId: item.id });
          });

          this.unselectItemsFrom(this.selectId);
        },
      });
    },
    initiateMoveItems(item = {}) {
      const { id, type } = item;

      this.$buefy.modal.open({
        parent: this,
        component: ModalMove,
        hasModalCard: true,
        trapFocus: true,
        props: {
          id,
        },
        events: {
          move: (collectionId) => {
            if (type === 'card') this.moveCard(collectionId, id);
            else if (type === 'note') this.moveNote(collectionId, id);
            else this.moveSelectedItems(collectionId);
          },
        },
      });
    },
    moveSelectedItems(newCollectionId) {
      this.selectedItems.forEach((selectedItem) => {
        if (selectedItem.type === 'card') this.moveCard(newCollectionId, selectedItem.id, true);
        else if (selectedItem.type === 'note') this.moveNote(newCollectionId, selectedItem.id, true);
      });

      this.unselectItemsFrom(this.selectId);
      this.unselectItemsFrom(newCollectionId);
    },
    moveCard(newCollectionId, id, doNotDeselect) {
      // For batch move actions, deselect per item is not necessary
      if (!doNotDeselect) this.toggleSelectedItem({ id: this.selectId, itemId: id, itemType: 'card', forceRemove: true });

      this.$updateCard({
        id,
        data: {
          relatedCollectionId: newCollectionId,
        },
      });
    },
    moveNote(newCollectionId, id, doNotDeselect) {
      // For batch move actions, deselect per item is not necessary
      if (!doNotDeselect) this.toggleSelectedItem({ id: this.selectId, itemId: id, itemType: 'note', forceRemove: true });

      this.moveCollectionNotes({ noteId: id, newCollectionId, dateLastModified: this.$getCurrentUTC() });
    },
  },
  mounted() {
    EventBus.$on('check-hide-context-menu', this.checkHideContextMenu);

    const scrollToPosition = this.$store.state.appState.scrollState.collectionView[this.selectId];
    if (scrollToPosition) this.$refs.itemsList.scrollTo(0, scrollToPosition);
  },
  watch: {
    async $route(to, from) {
      const keywordDeselected = to.name === 'KeywordList' && from.name === 'KeywordList' && to.path.length < from.path.length;
      const starDeselected = to.name === 'StarList' && from.name === 'StarList' && to.path.length < from.path.length;

      if (keywordDeselected || starDeselected) {
        // A keyword or star number is unselected, check whether we should unselect any card that isn't in the list anymore

        // First gather all displayed cards
        const displayedItems = this.itemsToDisplay.unsectioned.map(item => ({ type: item.type, id: item.id }));
        Object.values(this.itemsToDisplay.sectioned).forEach((section) => {
          section.forEach((item) => {
            displayedItems.push({ type: item.type, id: item.id });
          });
        });

        // Then filter out all items that are not displayed anymore and set new selectedItems
        const filteredSelectedItems = this.selectedItems.filter(selectedItem => displayedItems.find(item => (selectedItem.id === item.id && selectedItem.type === item.type)));
        this.selectItems({ id: this.selectId, items: filteredSelectedItems });
      }

      const scrollToPosition = this.$store.state.appState.scrollState.collectionView[this.selectId];
      await this.$nextTick();
      if (scrollToPosition) this.$refs.itemsList.scrollTo(0, scrollToPosition);
    },
  },
  beforeDestroy() {
    EventBus.$off('check-hide-context-menu', this.checkHideContextMenu);
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';

.content-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// NO ITEMS
.no-items {
  position: relative;
  width: 100%;
  z-index: 2;
  padding: 1rem;
  color: #aaaaaa;
  font-size: 0.8rem;
  cursor: default;
}
.add-note-or-card {
  color: $secondary;
  cursor: pointer;
}
.add-note-or-card:hover {
  text-decoration: underline;
}

// LIST VIEW
.items-list {
  position: relative;
  height: 1px; // Need to be specified for flexbox
  flex-grow: 1;
  flex-shrink: 2;
  padding: 0.25rem 0 25rem 0;
  overflow-y: scroll;
  overflow-x: hidden;
}
.section-header {
  font-size: 0.9rem;
  font-family: $quattrocentoSans;
  font-variant: small-caps;
  font-style: normal;
  color: $primary-dark;
  padding: 0 0 0 0.5rem;
  cursor: default;
  user-select: none;
}
.section-header-select {
  font-size: 0.75rem;
  font-style: italic;
  font-variant: normal;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}
.section-header:hover .section-header-select {
  opacity: 1;
}
.no-section {
  display: none;
  font-style: italic;
  margin-top: 0.5rem;
  padding: 0 0 0 0.25rem;
}
.sectioned-items:last-of-type .no-section {
  display: block;
}

// COLLECTION LIST ITEMS (FOR TRANSITION GROUP, RIGHT)
.collection-list-item {
  transition: all 0.2s;
  display: inline-block;
}
.collection-list-enter, .collection-list-leave-to {
  opacity: 0;
}
.collection-list-leave-active {
  position: absolute;
  opacity: 0;
  transition: none;
}
</style>
